import React, { FC } from 'react';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import { useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router-dom';
import { LEFT_COL_WIDTH } from 'utils/constants';

import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Divider, PageHeader, Row } from 'antd';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { MapModeEnum } from 'app/containers/Map/slice/types';

import * as S from './EditField.styles';
import { TextRegular12, TextRegular14 } from 'styles/typography';
import { selectEditFieldData } from '../../slice/selectors';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import useZoomOnFieldOnMapLoad from 'utils/hooks/useZoomOnFieldOnMapLoad';
import useLoadPolygons from 'utils/hooks/useLoadPolygons';
import useSetInitialEditFieldData from 'utils/hooks/useSetInitialEditFieldData';
import useOnUpdatedGeometryFieldSubmit from 'utils/hooks/useOnUpdatedGeometryFieldSubmit';

interface IReactRouterParams {
  fieldId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const EditField: FC<IProps> = ({ history, match: { params } }) => {
  const fieldIdInParams = params.fieldId;

  const { t } = useTranslation();
  const { mobileOrTablet } = useResponsiveQuery();

  const envId = useSelector(selectSelectedEnvId) as number;

  const { name, fieldId, readableArea } = useSelector(selectEditFieldData);

  useSetInitialEditFieldData({ fieldIdInParams });
  useLoadPolygons();
  useZoomOnFieldOnMapLoad({ fieldIdInParams });

  const { onSubmit } = useOnUpdatedGeometryFieldSubmit();

  const responsivePadding = mobileOrTablet ? 6 : 12;

  return (
    <>
      <PageHeader
        onBack={() => {
          if (fieldId) {
            history.push(
              generatePath(RouteConstants.field.details, {
                envId,
                fieldId,
              }),
            );
          }
        }}
        className="page-header"
        title={t('Field.EditField.Title', 'Edit field')}
        extra={[
          <Button type="primary" onClick={onSubmit}>
            {t('Field.CreateField.SaveBtn', 'Save')}
          </Button>,
        ]}
      />
      <Row
        wrap={mobileOrTablet}
        gutter={responsivePadding}
        style={{
          paddingLeft: responsivePadding,
          paddingTop: responsivePadding,
          width: '100%',
        }}
      >
        <Col
          style={{
            paddingBottom: 6,
            minWidth: mobileOrTablet ? '100%' : LEFT_COL_WIDTH,
          }}
        >
          <Card bordered={false}>
            <Row>
              <Col span={12}>
                <TextRegular14>
                  {t('Field.CreateField.FieldName', 'Name')}
                </TextRegular14>
              </Col>

              <Col span={12}>
                <TextRegular12>{name}</TextRegular12>
              </Col>
            </Row>

            <Divider dashed />
            <Row>
              <Col span={12}>
                <TextRegular14>
                  {t('Field.CreateField.Area', 'Area')}
                </TextRegular14>
              </Col>

              <Col span={12}>
                <TextRegular12>{readableArea || '-'}</TextRegular12>
              </Col>
            </Row>
          </Card>
        </Col>
        <S.MapCol flex="auto">
          <MapCard
            mode={MapModeEnum.editPolygon}
            readOnly
            showCulturesMenu={false}
          />
        </S.MapCol>
      </Row>
    </>
  );
};

export default EditField;
